"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useMemo, useCallback } from "react";
import { useRouter } from "next/navigation";
import { X, RotateCcw } from "lucide-react";
import { cn } from "@/utils/helpers";
import { Button } from "@/components/ui/button";
import { FilterField, FilterOption, FilterValueMap, ProductListingFilterProps } from "@/components/filter-panel/types";
import { expandSelection, updateTreeSelection } from "@/components/filter-panel/utils/tree-helpers";
import { cleanValue, computeFilterUrl, extractCleanCategoryId } from "@/components/filter-panel/utils/url-helpers";
import { useProductListingFilter } from "@/components/filter-panel/hooks/useProductListingFilter";
export interface FilterPillItem {
  id: string;
  label: string;
  count?: number;
  fieldId: string;
  field: FilterField;
  parentId: string | null;
  hasChildren: boolean;
  isSelected: boolean;
  depth: number;
}
export type MobileFilterPillsProps = ProductListingFilterProps;
const MobileFilterPills: React.FC<MobileFilterPillsProps> = props => {
  const $ = _c(48);
  const router = useRouter();
  const {
    filterSchema,
    isNavbarLabel,
    onClearFilters,
    currentCategory,
    isLoadingState,
    searchParams
  } = useProductListingFilter(props);
  let filters;
  if ($[0] !== filterSchema || $[1] !== searchParams) {
    filters = {};
    filterSchema.forEach(field => {
      const param = searchParams.get(field.id);
      if (param !== null) {
        if (field.type === "price" || field.type === "range") {
          const vals = param.split("-").map(Number).filter(_temp);
          if (vals.length === 2) {
            filters[field.id] = vals;
          }
        } else {
          if (field.type === "checkbox") {
            const vals_0 = param.split(",").map(_temp2).filter(Boolean);
            if (vals_0.length > 0) {
              filters[field.id] = expandSelection(field, vals_0);
            }
          } else {
            const val = decodeURIComponent(cleanValue(param));
            if (val) {
              filters[field.id] = val;
            }
          }
        }
      }
    });
    $[0] = filterSchema;
    $[1] = searchParams;
    $[2] = filters;
  } else {
    filters = $[2];
  }
  const activeFilters = filters;
  let t0;
  bb0: {
    for (const field_0 of filterSchema) {
      if (activeFilters[field_0.id] !== undefined) {
        const val_0 = activeFilters[field_0.id];
        if (Array.isArray(val_0) && val_0.length > 0) {
          t0 = true;
          break bb0;
        }
        if (!Array.isArray(val_0) && val_0 !== "") {
          t0 = true;
          break bb0;
        }
      }
    }
    if (searchParams.get("price")) {
      t0 = true;
      break bb0;
    }
    t0 = false;
  }
  const hasActiveFilters = t0;
  let list;
  if ($[3] !== activeFilters || $[4] !== filterSchema) {
    list = [];
    filterSchema.forEach(field_1 => {
      if (field_1.type === "checkbox" || field_1.type === "radio") {
        const selectedList = activeFilters[field_1.id] || [];
        const flatten = (items, t1, t2) => {
          const parentId = t1 === undefined ? null : t1;
          const depth = t2 === undefined ? 0 : t2;
          items.forEach(item => {
            const id = String(item.id);
            const isSelected = selectedList.includes(id);
            list.push({
              id,
              label: item.label || item.heading || item.title || "Untitled",
              count: item.count ?? item.product_count,
              fieldId: field_1.id,
              field: field_1,
              parentId,
              hasChildren: Boolean(item.children && item.children.length > 0 || item.subTypes && item.subTypes.length > 0),
              isSelected,
              depth
            });
            const children = item.children || item.subTypes;
            if (children && children.length > 0) {
              flatten(children, id, depth + 1);
            }
          });
        };
        if (field_1.options && field_1.options.length > 0) {
          flatten(field_1.options);
        }
      }
    });
    $[3] = activeFilters;
    $[4] = filterSchema;
    $[5] = list;
  } else {
    list = $[5];
  }
  const flattenedPills = list;
  let t1;
  if ($[6] !== flattenedPills) {
    const selected = [];
    const unselected = [];
    flattenedPills.forEach(pill => {
      if (pill.isSelected) {
        selected.push(pill);
      } else {
        unselected.push(pill);
      }
    });
    t1 = [...selected, ...unselected];
    $[6] = flattenedPills;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const displayPills = t1;
  let t2;
  if ($[8] !== currentCategory?.id || $[9] !== currentCategory?.url || $[10] !== filterSchema || $[11] !== isNavbarLabel || $[12] !== props.isCollection || $[13] !== router || $[14] !== searchParams) {
    t2 = nextFilters => {
      const {
        targetPath,
        newParams
      } = computeFilterUrl({
        filters: nextFilters,
        filterSchema,
        searchParams,
        parentCategoryUrl: currentCategory?.url,
        parentItemId: currentCategory?.id?.toString(),
        isCollection: props.isCollection,
        computedIsNavbarLabel: isNavbarLabel,
        parentCleanId: extractCleanCategoryId(currentCategory?.id?.toString(), currentCategory?.url),
        currentPathname: typeof window !== "undefined" ? window.location.pathname : ""
      });
      const newUrl = `${targetPath}${newParams ? `?${newParams}` : ""}`;
      window.dispatchEvent(new Event("route-change-start"));
      router.push(newUrl, {
        scroll: false
      });
    };
    $[8] = currentCategory?.id;
    $[9] = currentCategory?.url;
    $[10] = filterSchema;
    $[11] = isNavbarLabel;
    $[12] = props.isCollection;
    $[13] = router;
    $[14] = searchParams;
    $[15] = t2;
  } else {
    t2 = $[15];
  }
  const syncFiltersToUrl = t2;
  let t3;
  if ($[16] !== activeFilters || $[17] !== syncFiltersToUrl) {
    t3 = pill_0 => {
      const currentExpanded = activeFilters[pill_0.fieldId] || [];
      const nextExpanded = updateTreeSelection(pill_0.field, currentExpanded, pill_0.id, "select");
      const nextFilters_0 = {
        ...activeFilters,
        [pill_0.fieldId]: nextExpanded
      };
      syncFiltersToUrl(nextFilters_0);
    };
    $[16] = activeFilters;
    $[17] = syncFiltersToUrl;
    $[18] = t3;
  } else {
    t3 = $[18];
  }
  const handlePillSelect = t3;
  let t4;
  if ($[19] !== activeFilters || $[20] !== syncFiltersToUrl) {
    t4 = pill_1 => {
      const currentExpanded_0 = activeFilters[pill_1.fieldId] || [];
      const nextExpanded_0 = updateTreeSelection(pill_1.field, currentExpanded_0, pill_1.id, "deselect");
      const nextFilters_1 = {
        ...activeFilters,
        [pill_1.fieldId]: nextExpanded_0
      };
      syncFiltersToUrl(nextFilters_1);
    };
    $[19] = activeFilters;
    $[20] = syncFiltersToUrl;
    $[21] = t4;
  } else {
    t4 = $[21];
  }
  const handlePillRemove = t4;
  let t5;
  if ($[22] !== activeFilters || $[23] !== syncFiltersToUrl) {
    t5 = () => {
      const nextFilters_2 = {
        ...activeFilters
      };
      delete nextFilters_2["price"];
      syncFiltersToUrl(nextFilters_2);
    };
    $[22] = activeFilters;
    $[23] = syncFiltersToUrl;
    $[24] = t5;
  } else {
    t5 = $[24];
  }
  const handleRemovePrice = t5;
  if (isLoadingState) {
    let t6;
    if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <div data-testid="mobile-filter-pills-skeleton" className="w-full lg:hidden py-3 px-2 flex items-center gap-2 overflow-x-hidden"><div className="h-8 w-16 bg-stone-100 rounded-full animate-pulse flex-shrink-0" /><div className="h-8 w-24 bg-stone-100 rounded-full animate-pulse flex-shrink-0" /><div className="h-8 w-20 bg-stone-100 rounded-full animate-pulse flex-shrink-0" /><div className="h-8 w-28 bg-stone-100 rounded-full animate-pulse flex-shrink-0" /></div>;
      $[25] = t6;
    } else {
      t6 = $[25];
    }
    return t6;
  }
  if (flattenedPills.length === 0 && !hasActiveFilters) {
    return null;
  }
  const t6 = !hasActiveFilters;
  const t7 = hasActiveFilters ? "bg-stone-100 hover:bg-stone-200 text-stone-700 border border-stone-300 active:scale-95 shadow-xs cursor-pointer" : "bg-stone-50 text-stone-300 border border-stone-200/70 cursor-not-allowed opacity-50";
  let t8;
  if ($[26] !== t7) {
    t8 = cn("h-[34px] flex-shrink-0 inline-flex items-center gap-1.5 px-3 rounded-lg text-xs font-outfit font-semibold uppercase tracking-wider transition-all select-none focus:outline-none focus-visible:ring-2 focus-visible:ring-[#037A6A]", t7);
    $[26] = t7;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  const t9 = hasActiveFilters ? "text-stone-600" : "text-stone-300";
  let t10;
  if ($[28] !== t9) {
    t10 = <RotateCcw size={12} className={t9} />;
    $[28] = t9;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <span>Clear</span>;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== onClearFilters || $[32] !== t10 || $[33] !== t6 || $[34] !== t8) {
    t12 = <Button type="button" variant="outline" size="sm" onClick={onClearFilters} disabled={t6} aria-label="Clear all filters" className={t8}>{t10}{t11}</Button>;
    $[31] = onClearFilters;
    $[32] = t10;
    $[33] = t6;
    $[34] = t8;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  let t13;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <div className="w-[1px] h-5 bg-stone-200 flex-shrink-0 self-center" aria-hidden="true" />;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  let t14;
  if ($[37] !== activeFilters.price || $[38] !== handleRemovePrice) {
    t14 = activeFilters.price && Array.isArray(activeFilters.price) && <div key="price-pill" className="h-[34px] flex-shrink-0 inline-flex items-center rounded-lg bg-[#037A6A] text-white border border-[#037A6A] shadow-xs transition-all text-xs font-outfit font-medium active:scale-[0.98]"><span className="pl-3 pr-1 select-none">₹{activeFilters.price[0]} - ₹{activeFilters.price[1]}</span><Button type="button" variant="ghost" size="sm" onClick={handleRemovePrice} aria-label="Remove price filter" className="h-full p-1 mr-1 hover:bg-white/20 hover:text-white rounded text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white"><X size={13} className="stroke-[2.5]" /></Button></div>;
    $[37] = activeFilters.price;
    $[38] = handleRemovePrice;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  let t15;
  if ($[40] !== displayPills || $[41] !== handlePillRemove || $[42] !== handlePillSelect) {
    t15 = displayPills.map(pill_2 => {
      if (pill_2.isSelected) {
        return <div key={`${pill_2.fieldId}-${pill_2.id}`} data-testid={`pill-${pill_2.id}-selected`} className="h-[34px] flex-shrink-0 inline-flex items-center rounded-lg bg-[#037A6A] text-white border border-[#037A6A] shadow-xs transition-all text-xs font-outfit font-medium active:scale-[0.98]"><Button type="button" variant="ghost" size="sm" onClick={() => handlePillSelect(pill_2)} aria-label={`Filter: ${pill_2.label}`} title={pill_2.label} className="h-full px-0 pl-3 pr-1 text-left inline-flex items-center gap-1 select-none text-white hover:text-white hover:bg-transparent focus:outline-none focus-visible:ring-2 focus-visible:ring-white rounded-l-lg rounded-r-none text-xs font-normal"><span className="max-w-[150px] truncate">{pill_2.label}</span>{pill_2.count !== undefined && pill_2.count > 0 && <span className="text-[11px] text-white/80 font-normal">({pill_2.count})</span>}</Button><Button type="button" variant="ghost" size="sm" onClick={e => {
            e.stopPropagation();
            handlePillRemove(pill_2);
          }} aria-label={`Remove ${pill_2.label} filter`} className="h-full p-1 mr-1 hover:bg-white/20 hover:text-white rounded text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white rounded-l-none"><X size={13} className="stroke-[2.5]" /></Button></div>;
      }
      return <Button key={`${pill_2.fieldId}-${pill_2.id}`} type="button" variant="outline" size="sm" data-testid={`pill-${pill_2.id}-unselected`} onClick={() => handlePillSelect(pill_2)} aria-label={`Select ${pill_2.label} filter`} title={pill_2.label} className="h-[34px] flex-shrink-0 inline-flex items-center gap-1 px-3 rounded-lg border border-[#037A6A] bg-white hover:bg-[#037A6A]/5 text-[#037A6A] hover:text-[#037A6A] transition-all text-xs font-outfit font-medium active:scale-[0.98] select-none shadow-xs focus:outline-none focus-visible:ring-2 focus-visible:ring-[#037A6A]"><span className="max-w-[150px] truncate">{pill_2.label}</span>{pill_2.count !== undefined && pill_2.count > 0 && <span className="text-[11px] text-[#037A6A]/70 font-normal">({pill_2.count})</span>}</Button>;
    });
    $[40] = displayPills;
    $[41] = handlePillRemove;
    $[42] = handlePillSelect;
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  let t16;
  if ($[44] !== t12 || $[45] !== t14 || $[46] !== t15) {
    t16 = <div data-testid="mobile-filter-pills" className="w-full lg:hidden pb-2 mb-2"><div role="region" aria-label="Filter options" className="flex items-center gap-2 overflow-x-auto no-scrollbar scrollbar-hide py-1 pl-0 pr-4 touch-pan-x flex-nowrap w-full">{t12}{t13}{t14}{t15}</div></div>;
    $[44] = t12;
    $[45] = t14;
    $[46] = t15;
    $[47] = t16;
  } else {
    t16 = $[47];
  }
  return t16;
};
export default MobileFilterPills;
function _temp(n) {
  return !isNaN(n);
}
function _temp2(v) {
  return decodeURIComponent(cleanValue(v));
}